import React, { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { X, UploadCloud, Video, Trash2 } from 'lucide-react';

const UploadVideoModal = ({ onClose, baseUrl, authToken }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      Swal.fire({ icon: 'warning', title: 'Invalid File', text: 'Please select a valid video file.' });
      return;
    }

    const maxSizeMB = 100;
    if (file.size / (1024 * 1024) > maxSizeMB) {
      Swal.fire({ icon: 'warning', title: 'Too Large', text: `Video must be under ${maxSizeMB}MB.` });
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleRemove = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = () => {
    if (!videoFile) return;

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('video', videoFile);

    // 🔸 Dummy call — apna real endpoint yahan lagana
    axios.post(`${baseUrl}/adminhome/uploadvideo`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (evt) => {
        const percent = Math.round((evt.loaded * 100) / evt.total);
        setProgress(percent);
      }
    })
      .then((res) => {
        Swal.fire({
          icon: 'success',
          title: 'Video Uploaded!',
          text: 'Your video has been uploaded successfully.',
          timer: 2200,
          showConfirmButton: false
        });
        onClose();
      })
      .catch((err) => {
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed',
          text: err.response?.data?.message || 'Something went wrong while uploading.'
        });
      })
      .finally(() => setUploading(false));
  };

  return (
    <div className="uvm-overlay" onClick={onClose}>
      <div className="uvm-modal" onClick={(e) => e.stopPropagation()}>

        <div className="uvm-modal__header">
          <div className="uvm-modal__icon"><Video size={20} /></div>
          <div>
            <h3 className="uvm-modal__title">Upload Video</h3>
            <p className="uvm-modal__sub">Add or update your dashboard video</p>
          </div>
          <button className="uvm-modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="uvm-modal__body">

          {!videoPreview ? (
            <div
              className={'uvm-upload-zone' + (isDragging ? ' uvm-upload-zone--dragging' : '')}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                hidden
                onChange={handleInputChange}
              />
              <div className="uvm-upload-zone__icon">
                <UploadCloud size={26} />
              </div>
              <h4 className="uvm-upload-zone__title">Click or drag video here</h4>
              <p className="uvm-upload-zone__sub">MP4, MOV or WebM — up to 100MB</p>
            </div>
          ) : (
            <div className="uvm-preview">
              <video src={videoPreview} className="uvm-preview__video" controls />

              {uploading && (
                <div className="uvm-progress">
                  <div className="uvm-progress__bar" style={{ width: progress + '%' }} />
                  <span className="uvm-progress__label">{progress}%</span>
                </div>
              )}

              <button className="uvm-remove-btn" onClick={handleRemove} disabled={uploading}>
                <Trash2 size={14} /> Remove
              </button>
            </div>
          )}

        </div>

        <div className="uvm-modal__footer">
          <button className="uvm-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="uvm-submit-btn"
            onClick={handleUpload}
            disabled={!videoFile || uploading}
          >
            {uploading ? <span className="uvm-spinner" /> : <><UploadCloud size={15} /> Upload</>}
          </button>
        </div>

      </div>
    </div>
  );
};

export default UploadVideoModal;